import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Search } from 'lucide-react';
import { useToast } from '@/lib/hooks/use-toast';
import { importTrackingService } from '../services/import-tracking-service';
import type {
  ImportOrderLeg,
  TransportMode,
  ImportLegStatus,
} from '../types/import-tracking.types';
import { LEG_STATUS_LABEL } from '../types/import-tracking.types';
import type { CatalogFreightLeg } from '@/features/master-data/services/freight-leg-catalog-service';
import { LegCatalogPicker } from './leg-catalog-picker';
import { SearchableSelect } from '@/components/ui/searchable-select';
import {
  freightLocationsService,
  FREIGHT_LOCATION_TYPE_LABELS,
  type FreightLocation,
} from '@/features/master-data/services/freight-locations-service';

const TRANSPORT_MODE_LABEL: Record<TransportMode, string> = {
  SEA: 'Marítimo',
  AIR: 'Aéreo',
  LAND: 'Terrestre',
  RAIL: 'Ferroviario',
  MULTIMODAL: 'Multimodal',
};

const LEG_TYPE_LABEL: Record<string, string> = {
  INLAND_ORIGIN: 'Inland origen',
  INTERNATIONAL: 'Internacional',
  INLAND_DESTINATION: 'Inland destino',
  HANDLING: 'Handling',
};

type Mode = { kind: 'create'; orderId: string } | { kind: 'edit'; leg: ImportOrderLeg };

interface LegFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: Mode;
  onSuccess: () => void;
}

export function LegFormDialog({ open, onOpenChange, mode, onSuccess }: LegFormDialogProps) {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const handlePick = (catalogLeg: CatalogFreightLeg) => {
    setOrigin(catalogLeg.origin);
    setDestination(catalogLeg.destination);
    setOriginLocationId(
      (catalogLeg as { originLocationId?: string | null }).originLocationId ?? ''
    );
    setDestinationLocationId(
      (catalogLeg as { destinationLocationId?: string | null }).destinationLocationId ?? ''
    );
    setTransportMode(catalogLeg.transportMode);
    setLegType(catalogLeg.legType);
    setCarrier(catalogLeg.provider?.name ?? '');
    setLegCatalogId(catalogLeg.id);
    setProviderId(catalogLeg.providerId ?? '');
    // Note: don't auto-set tracking ref or notes — user fills those per-shipment
    // Don't touch dates either
    setPickerOpen(false);
    toast({
      title: 'Tramo cargado del catálogo',
      description: `${catalogLeg.origin} → ${catalogLeg.destination}`,
    });
  };

  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [originLocationId, setOriginLocationId] = useState<string>('');
  const [destinationLocationId, setDestinationLocationId] = useState<string>('');
  const [locations, setLocations] = useState<FreightLocation[]>([]);
  const [transportMode, setTransportMode] = useState<TransportMode>('SEA');
  const [legType, setLegType] = useState<
    'INLAND_ORIGIN' | 'INTERNATIONAL' | 'INLAND_DESTINATION' | 'HANDLING'
  >('INTERNATIONAL');
  const [status, setStatus] = useState<ImportLegStatus>('PENDING');
  const [expectedStart, setExpectedStart] = useState('');
  const [expectedEnd, setExpectedEnd] = useState('');
  const [carrier, setCarrier] = useState('');
  const [trackingRef, setTrackingRef] = useState('');
  const [notes, setNotes] = useState('');
  // Vínculo al catálogo de tramos: necesario para que el LegPricingDialog
  // pueda traer la tarifa parametrizada del proveedor para este tramo.
  const [legCatalogId, setLegCatalogId] = useState<string>('');
  const [providerId, setProviderId] = useState<string>('');

  // Inicialización: depender de valores estables (open + kind + leg.id) en vez
  // del objeto `mode` completo, para no resetear cuando el parent re-rendere
  // y mande un objeto `mode` nuevo (sucede al abrir el LegCatalogPicker).
  const editLegId = mode.kind === 'edit' ? mode.leg.id : null;
  useEffect(() => {
    if (!open) return;
    if (mode.kind === 'edit') {
      const l = mode.leg;
      setOrigin(l.origin);
      setDestination(l.destination);
      setOriginLocationId((l as { originLocationId?: string | null }).originLocationId ?? '');
      setDestinationLocationId(
        (l as { destinationLocationId?: string | null }).destinationLocationId ?? ''
      );
      setTransportMode(l.transportMode);
      setLegType(l.legType);
      setStatus(l.status);
      setExpectedStart(l.expectedStart?.slice(0, 16) ?? '');
      setExpectedEnd(l.expectedEnd?.slice(0, 16) ?? '');
      setCarrier(l.carrier ?? '');
      setTrackingRef(l.trackingRef ?? '');
      setNotes(l.notes ?? '');
      setLegCatalogId(l.legCatalogId ?? '');
      setProviderId(l.providerId ?? '');
    } else {
      setOrigin('');
      setDestination('');
      setOriginLocationId('');
      setDestinationLocationId('');
      setTransportMode('SEA');
      setLegType('INTERNATIONAL');
      setStatus('PENDING');
      setExpectedStart('');
      setExpectedEnd('');
      setCarrier('');
      setTrackingRef('');
      setNotes('');
      setLegCatalogId('');
      setProviderId('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode.kind, editLegId]);

  // Cargar localizaciones del tenant cuando el dialog se abre
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    freightLocationsService
      .list({ isActive: true })
      .then((data) => {
        if (!cancelled) setLocations(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Helper para elegir una localización: pisa el ID y el string snapshot
  const pickOrigin = (id: string) => {
    setOriginLocationId(id);
    const loc = locations.find((l) => l.id === id);
    if (loc) setOrigin(loc.name);
  };
  const pickDestination = (id: string) => {
    setDestinationLocationId(id);
    const loc = locations.find((l) => l.id === id);
    if (loc) setDestination(loc.name);
  };

  const handleSubmit = async () => {
    if (!origin.trim() || !destination.trim()) {
      toast({ title: 'Origen y destino son obligatorios', variant: 'destructive' });
      return;
    }

    setSubmitting(true);
    try {
      const isoStart = expectedStart ? new Date(expectedStart).toISOString() : null;
      const isoEnd = expectedEnd ? new Date(expectedEnd).toISOString() : null;

      if (mode.kind === 'create') {
        await importTrackingService.createLeg(mode.orderId, {
          origin: origin.trim(),
          destination: destination.trim(),
          originLocationId: originLocationId || null,
          destinationLocationId: destinationLocationId || null,
          transportMode,
          legType,
          expectedStart: isoStart,
          expectedEnd: isoEnd,
          carrier: carrier.trim() || null,
          trackingRef: trackingRef.trim() || null,
          notes: notes.trim() || null,
          legCatalogId: legCatalogId || null,
          providerId: providerId || null,
        } as Parameters<typeof importTrackingService.createLeg>[1]);
        toast({ title: 'Tramo creado' });
      } else {
        await importTrackingService.updateLeg(mode.leg.id, {
          origin: origin.trim(),
          destination: destination.trim(),
          originLocationId: originLocationId || null,
          destinationLocationId: destinationLocationId || null,
          transportMode,
          legType,
          status,
          expectedStart: isoStart,
          expectedEnd: isoEnd,
          carrier: carrier.trim() || null,
          trackingRef: trackingRef.trim() || null,
          notes: notes.trim() || null,
        } as Parameters<typeof importTrackingService.updateLeg>[1]);
        toast({ title: 'Tramo actualizado' });
      }
      onSuccess();
      onOpenChange(false);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error al guardar tramo';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent aria-describedby={undefined} className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {mode.kind === 'create' ? 'Nuevo tramo' : `Editar tramo ${mode.leg.legOrder}`}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            {mode.kind === 'create' && (
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md border-2 border-dashed border-primary/40 text-sm text-primary hover:bg-primary/5 hover:border-primary transition-colors"
              >
                <Search className="w-4 h-4" />
                Buscar en catálogo de tramos
              </button>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Origen</Label>
                <SearchableSelect
                  value={originLocationId}
                  onValueChange={pickOrigin}
                  placeholder="Seleccionar localización..."
                  searchPlaceholder="Buscar..."
                  emptyMessage="Sin localizaciones (cargá una en datos maestros)"
                  options={locations.map((l) => ({
                    value: l.id,
                    label: `${l.code} · ${l.name}${l.city ? ` (${l.city})` : ''} — ${FREIGHT_LOCATION_TYPE_LABELS[l.type]}`,
                  }))}
                />
                {origin && !originLocationId && (
                  <p className="text-[10px] text-muted-foreground italic">
                    Origen actual: {origin} (sin link a catálogo)
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <Label>Destino</Label>
                <SearchableSelect
                  value={destinationLocationId}
                  onValueChange={pickDestination}
                  placeholder="Seleccionar localización..."
                  searchPlaceholder="Buscar..."
                  emptyMessage="Sin localizaciones (cargá una en datos maestros)"
                  options={locations.map((l) => ({
                    value: l.id,
                    label: `${l.code} · ${l.name}${l.city ? ` (${l.city})` : ''} — ${FREIGHT_LOCATION_TYPE_LABELS[l.type]}`,
                  }))}
                />
                {destination && !destinationLocationId && (
                  <p className="text-[10px] text-muted-foreground italic">
                    Destino actual: {destination} (sin link a catálogo)
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Modo de transporte</Label>
                <Select
                  value={transportMode}
                  onValueChange={(v) => setTransportMode(v as TransportMode)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TRANSPORT_MODE_LABEL).map(([k, label]) => (
                      <SelectItem key={k} value={k}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Tipo de tramo</Label>
                <Select value={legType} onValueChange={(v) => setLegType(v as typeof legType)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(LEG_TYPE_LABEL).map(([k, label]) => (
                      <SelectItem key={k} value={k}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {mode.kind === 'edit' && (
              <div className="space-y-1">
                <Label>Estado</Label>
                <Select value={status} onValueChange={(v) => setStatus(v as ImportLegStatus)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(LEG_STATUS_LABEL).map(([k, label]) => (
                      <SelectItem key={k} value={k}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Fecha esperada inicio</Label>
                <Input
                  type="datetime-local"
                  value={expectedStart}
                  onChange={(e) => setExpectedStart(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label>Fecha esperada fin</Label>
                <Input
                  type="datetime-local"
                  value={expectedEnd}
                  onChange={(e) => setExpectedEnd(e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Transportista</Label>
                <Input
                  value={carrier}
                  onChange={(e) => setCarrier(e.target.value)}
                  placeholder="COSCO, DHL..."
                />
              </div>
              <div className="space-y-1">
                <Label>Referencia tracking</Label>
                <Input
                  value={trackingRef}
                  onChange={(e) => setTrackingRef(e.target.value)}
                  placeholder="BL/AWB/Guia"
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label>Notas</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting
                ? 'Guardando...'
                : mode.kind === 'create'
                  ? 'Crear tramo'
                  : 'Guardar cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LegCatalogPicker open={pickerOpen} onOpenChange={setPickerOpen} onPick={handlePick} />
    </>
  );
}
